import React from 'react';
import { Typography, Row, Col, Card, Space, Divider, Timeline, Avatar, Statistic, Button } from 'antd';
import { 
  TeamOutlined, 
  TrophyOutlined, 
  RocketOutlined, 
  HistoryOutlined,
  MessageOutlined,
  ShopOutlined,
  UserOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;

const About = () => {
  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-400 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <Title level={1} className="text-white mb-6">
            À propos de WABot
          </Title>
          <Paragraph className="text-lg opacity-90 max-w-2xl mx-auto">
            WABot révolutionne la façon dont les entreprises africaines communiquent avec leurs clients 
            en automatisant les conversations WhatsApp pour un service plus rapide et efficace.
          </Paragraph>
        </div>
      </section>

      {/* Notre histoire */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <Row gutter={[48, 48]} align="middle">
            <Col xs={24} md={12}>
              <Title level={2} className="mb-6">
                Notre histoire
              </Title>
              <Paragraph className="text-lg">
                WABot est né en 2022 d'un constat simple : les entreprises africaines utilisent massivement 
                WhatsApp pour communiquer avec leurs clients, mais manquent d'outils adaptés pour gérer 
                efficacement ce canal.
              </Paragraph>
              <Paragraph className="text-lg">
                Notre fondateur, après avoir dirigé plusieurs entreprises de commerce en ligne en Afrique de l'Ouest, 
                a été confronté à la difficulté de gérer des centaines de conversations WhatsApp quotidiennes. 
                Face à l'absence de solutions adaptées au marché africain, il a décidé de créer WABot.
              </Paragraph>
              <Paragraph className="text-lg">
                Depuis, notre mission est d'aider les entreprises africaines à exploiter pleinement le potentiel 
                de WhatsApp pour développer leur activité, en offrant une solution complète adaptée aux spécificités 
                du marché local.
              </Paragraph>
            </Col>
            <Col xs={24} md={12}>
              <div className="rounded-lg overflow-hidden shadow-xl">
                <img 
                  src="/about-story.jpg" 
                  alt="L'équipe WABot" 
                  className="w-full h-auto"
                />
              </div>
            </Col>
          </Row>
        </div>
      </section>

      {/* Statistiques */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <Title level={2} className="text-center mb-12">
            WABot en chiffres
          </Title>
          
          <Row gutter={[32, 32]} justify="center">
            <Col xs={24} sm={12} md={6}>
              <Card className="text-center h-full shadow-md">
                <Statistic 
                  title="Entreprises" 
                  value={500} 
                  prefix={<ShopOutlined />}
                  className="mb-3"
                />
                <Text className="text-gray-500">entreprises utilisent WABot</Text>
              </Card>
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              <Card className="text-center h-full shadow-md">
                <Statistic 
                  title="Messages" 
                  value={1.5} 
                  suffix="M" 
                  precision={1}
                  prefix={<MessageOutlined />}
                  className="mb-3"
                />
                <Text className="text-gray-500">messages traités par mois</Text>
              </Card>
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              <Card className="text-center h-full shadow-md">
                <Statistic 
                  title="Pays" 
                  value={12} 
                  prefix={<RocketOutlined />}
                  className="mb-3"
                />
                <Text className="text-gray-500">pays d'Afrique couverts</Text>
              </Card>
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              <Card className="text-center h-full shadow-md">
                <Statistic 
                  title="Clients finaux" 
                  value={2.8} 
                  suffix="M"
                  precision={1}
                  prefix={<UserOutlined />}
                  className="mb-3"
                />
                <Text className="text-gray-500">clients finaux touchés</Text>
              </Card>
            </Col>
          </Row>
        </div>
      </section>

      {/* Notre parcours */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <Title level={2} className="text-center mb-12">
            Notre parcours
          </Title>
          
          <div className="max-w-3xl mx-auto">
            <Timeline
              mode="alternate"
              items={[
                {
                  dot: <HistoryOutlined className="text-blue-500" />,
                  children: (
                    <Card className="timeline-card shadow-md mb-8">
                      <Title level={4}>2022 : Naissance de l'idée</Title>
                      <Paragraph>
                        Face aux défis de gestion des conversations WhatsApp, l'idée de WABot prend forme pour 
                        répondre aux besoins spécifiques des entreprises africaines.
                      </Paragraph>
                    </Card>
                  ),
                },
                {
                  dot: <RocketOutlined className="text-blue-500" />,
                  children: (
                    <Card className="timeline-card shadow-md mb-8">
                      <Title level={4}>2022 : Lancement de la version beta</Title>
                      <Paragraph>
                        Après 6 mois de développement, la première version de WABot est lancée avec 
                        50 entreprises pilotes.
                      </Paragraph>
                    </Card>
                  ),
                },
                {
                  dot: <TrophyOutlined className="text-blue-500" />,
                  children: (
                    <Card className="timeline-card shadow-md mb-8">
                      <Title level={4}>2023 : Premier prix d'innovation</Title>
                      <Paragraph>
                        WABot reçoit le prix de l'innovation technologique lors du Salon de l'Entrepreneuriat 
                        Digital d'Afrique de l'Ouest.
                      </Paragraph>
                    </Card>
                  ),
                },
                {
                  dot: <ShopOutlined className="text-blue-500" />,
                  children: (
                    <Card className="timeline-card shadow-md mb-8">
                      <Title level={4}>2023 : Intégration Shopify</Title>
                      <Paragraph>
                        Lancement de l'intégration avec Shopify pour synchroniser automatiquement les 
                        produits et commandes.
                      </Paragraph>
                    </Card>
                  ),
                },
                {
                  dot: <TeamOutlined className="text-blue-500" />,
                  children: (
                    <Card className="timeline-card shadow-md">
                      <Title level={4}>2024 : Expansion régionale</Title>
                      <Paragraph>
                        WABot s'étend à 12 pays d'Afrique et franchit le cap des 500 entreprises clientes.
                      </Paragraph>
                    </Card>
                  ),
                },
              ]}
            />
          </div>
        </div>
      </section>

      {/* Notre équipe */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <Title level={2} className="text-center mb-12">
            Notre équipe
          </Title>
          
          <Row gutter={[32, 32]} justify="center">
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card className="text-center h-full shadow-md">
                <Avatar size={100} src="/team-ceo.jpg" className="mb-4" />
                <Title level={4}>Amadou Diallo</Title>
                <Text className="text-gray-500 block mb-4">Fondateur & CEO</Text>
                <Paragraph>
                  Entrepreneur en série avec 15 ans d'expérience dans le e-commerce en Afrique.
                </Paragraph>
              </Card>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card className="text-center h-full shadow-md">
                <Avatar size={100} src="/team-cto.jpg" className="mb-4" />
                <Title level={4}>Marie Touré</Title>
                <Text className="text-gray-500 block mb-4">CTO</Text>
                <Paragraph>
                  Experte en tech avec un parcours chez Microsoft et des startups africaines.
                </Paragraph>
              </Card>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card className="text-center h-full shadow-md">
                <Avatar size={100} src="/team-cmo.jpg" className="mb-4" />
                <Title level={4}>Seydou Koné</Title>
                <Text className="text-gray-500 block mb-4">CMO</Text>
                <Paragraph>
                  Ancien directeur marketing chez Orange avec une expertise en stratégie digitale.
                </Paragraph>
              </Card>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card className="text-center h-full shadow-md">
                <Avatar size={100} src="/team-cco.jpg" className="mb-4" />
                <Title level={4}>Fatou Bah</Title>
                <Text className="text-gray-500 block mb-4">CCO</Text>
                <Paragraph>
                  Spécialiste de l'expérience client avec une passion pour l'innovation.
                </Paragraph>
              </Card>
            </Col>
          </Row>
        </div>
      </section>

      {/* Notre vision */}
      <section className="py-16">
        <div className="container mx-auto px-4 text-center">
          <Title level={2} className="mb-6">
            Notre vision
          </Title>
          <Paragraph className="text-lg max-w-3xl mx-auto mb-8">
            Nous croyons que le commerce digital en Afrique a un potentiel immense, mais qu'il doit 
            s'adapter aux réalités et préférences locales. WhatsApp est devenu le canal privilégié de 
            communication pour les entreprises africaines, et notre vision est de leur fournir les 
            outils les plus performants pour exploiter ce canal à son plein potentiel.
          </Paragraph>
          <Paragraph className="text-lg max-w-3xl mx-auto">
            À terme, nous voulons être la plateforme de référence qui connecte les entreprises africaines 
            à leurs clients, en rendant le commerce digital plus accessible, plus efficace et plus humain.
          </Paragraph>
        </div>
      </section>

      {/* Valeurs */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <Title level={2} className="text-center mb-12">
            Nos valeurs
          </Title>
          
          <Row gutter={[32, 32]}>
            <Col xs={24} md={8}>
              <Card className="h-full shadow-md">
                <div className="text-center mb-6">
                  <div className="bg-blue-100 text-blue-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <RocketOutlined style={{ fontSize: 28 }} />
                  </div>
                  <Title level={3}>Innovation locale</Title>
                </div>
                <Paragraph>
                  Nous développons des solutions spécifiquement adaptées aux défis et besoins du marché africain, 
                  en prenant en compte les réalités locales comme la connectivité variable et les préférences d'usage.
                </Paragraph>
              </Card>
            </Col>
            
            <Col xs={24} md={8}>
              <Card className="h-full shadow-md">
                <div className="text-center mb-6">
                  <div className="bg-blue-100 text-blue-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <TeamOutlined style={{ fontSize: 28 }} />
                  </div>
                  <Title level={3}>Autonomisation</Title>
                </div>
                <Paragraph>
                  Nous croyons au potentiel des entreprises africaines et notre mission est de leur donner les 
                  outils pour se développer et concurrencer à l'échelle mondiale, en simplifiant leur accès 
                  à la technologie.
                </Paragraph>
              </Card>
            </Col>
            
            <Col xs={24} md={8}>
              <Card className="h-full shadow-md">
                <div className="text-center mb-6">
                  <div className="bg-blue-100 text-blue-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <TrophyOutlined style={{ fontSize: 28 }} />
                  </div>
                  <Title level={3}>Excellence</Title>
                </div>
                <Paragraph>
                  Nous visons l'excellence dans chaque aspect de notre service, de la performance technique 
                  à l'expérience utilisateur, en passant par le support client. Nous nous efforçons constamment 
                  d'améliorer notre plateforme.
                </Paragraph>
              </Card>
            </Col>
          </Row>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <Title level={2} className="text-white mb-6">
            Rejoignez l'aventure WABot
          </Title>
          <Paragraph className="text-lg mb-8 max-w-2xl mx-auto">
            Faites partie des entreprises qui transforment leur relation client avec WABot.
            Commencez votre essai gratuit dès aujourd'hui.
          </Paragraph>
          <Space size="middle">
            <Link to="/register">
              <Button type="default" size="large" shape="round">
                Essayer gratuitement
              </Button>
            </Link>
          </Space>
        </div>
      </section>
    </div>
  );
};

export default About; 